import { useState } from 'react';
import { Link } from 'react-router-dom';
import Tippy from '@tippyjs/react/headless';
import { useAuth } from '@/hooks/useAuth';
import { useMembershipStore, useAssetRenewStore } from '@/stores';

export interface UserMenuProps {
  className?: string;
  onOpenVipModal?: () => void;
  /** 弹出位置方向，不传则由 Tippy 自动控制 */
  placement?: 'auto' | 'auto-start' | 'auto-end' | 'top' | 'top-start' | 'top-end' | 'bottom' | 'bottom-start' | 'bottom-end' | 'right' | 'right-start' | 'right-end' | 'left' | 'left-start' | 'left-end';
}

/**
 * 导航栏用户个人菜单下拉组件
 */
export function UserMenu({ className = '', onOpenVipModal }: UserMenuProps) {
  const { user, logout, isEnterprise } = useAuth();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userAvatarCache = useAssetRenewStore((state) => state.userAvatarCache);

  const handleLogout = () => {
    logout();
    setUserMenuOpen(false);
  };

  if (!user) return null;

  const avatarUrl = userAvatarCache?.url || (typeof user.avatar === 'object'
    ? (user.avatar as any)?.access_url
    : (typeof user.avatar === 'string' && (user.avatar.startsWith('http') || user.avatar.startsWith('data:') || user.avatar.startsWith('blob:') || user.avatar.includes('/'))
      ? user.avatar
      : undefined));


  const handleOpenVipModal = () => {
    setUserMenuOpen(false);
    if (onOpenVipModal) {
      onOpenVipModal();
    } else {
      useMembershipStore.getState().openMembershipModal();
    }
  };

  return (
    <div className={`relative flex-shrink-0 ${className}`}>
      <Tippy
        interactive={true}
        visible={userMenuOpen}
        onClickOutside={() => setUserMenuOpen(false)}
        placement="bottom-end"
        offset={[0, 10]}
        appendTo={() => document.body}
        render={(attrs) => (
          <div
            {...attrs}
            className="w-56 bg-white/95 backdrop-blur-xl border border-white/90 rounded-2xl p-2 shadow-2xl animate-[fadeIn_0.15s_ease-out] z-50 outline-none"
          >
            <div className="px-4 py-3 border-b border-slate-100 mb-1">
              <p className="font-bold text-slate-900 text-sm truncate">{user.name}</p>
              <p className="text-xs text-slate-500 truncate mt-0.5">{user.phone || user.email || '普通用户'}</p>
            </div>
            <Link
              to="/profile"
              onClick={() => setUserMenuOpen(false)}
              className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm text-slate-700 hover:text-emerald-700 hover:bg-emerald-50 transition-all cursor-pointer"
            >
              <i className="ri-user-line text-lg"></i>
              个人中心
            </Link>
            <button
              type="button"
              onClick={handleOpenVipModal}
              className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm text-slate-700 hover:text-emerald-700 hover:bg-emerald-50 transition-all cursor-pointer text-left"
            >
              <i className="ri-vip-crown-line text-lg text-amber-500"></i>
              会员中心
            </button>
            <Link
              to="/projects"
              onClick={() => setUserMenuOpen(false)}
              className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm text-slate-700 hover:text-emerald-700 hover:bg-emerald-50 transition-all cursor-pointer"
            >
              <i className="ri-rocket-line text-lg"></i>
              发行项目
            </Link>
            <Link
              to={isEnterprise ? '/my-postings' : '/my-bids'}
              onClick={() => setUserMenuOpen(false)}
              className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm text-slate-700 hover:text-emerald-700 hover:bg-emerald-50 transition-all cursor-pointer"
            >
              <i className="ri-file-list-3-line text-lg"></i>
              我的需求
            </Link>
            {isEnterprise && (
              <Link
                to="/my/job-postings"
                onClick={() => setUserMenuOpen(false)}
                className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm text-slate-700 hover:text-emerald-700 hover:bg-emerald-50 transition-all cursor-pointer"
              >
                <i className="ri-user-search-line text-lg"></i>
                招聘管理
              </Link>
            )}
            <div className="border-t border-slate-100 my-1.5"></div>
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition-all cursor-pointer text-left"
            >
              <i className="ri-logout-box-line text-lg"></i>
              退出登录
            </button>
          </div>
        )}
      >
        <button
          onClick={() => setUserMenuOpen(!userMenuOpen)}
          className="flex items-center gap-0 xl:gap-2 p-0.5 xl:px-3 xl:py-1.5 rounded-full hover:bg-slate-100/80 xl:border xl:border-slate-200/60 transition-all cursor-pointer flex-shrink-0"
        >
          <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-gradient-to-tr from-emerald-400 to-teal-500 overflow-hidden flex items-center justify-center shadow-sm flex-shrink-0 ring-2 ring-slate-100 hover:ring-emerald-400/60 transition-all">
            {avatarUrl ? (
              <img src={avatarUrl} alt={user.name} className="w-full h-full object-cover rounded-full" />
            ) : (
              <span className="text-white font-semibold text-xs lg:text-sm">{(typeof user.avatar === 'string' ? user.avatar : '') || user.name.slice(0, 1)}</span>
            )}
          </div>
          <span className="hidden xl:inline-block text-xs lg:text-sm xl:text-base font-medium text-slate-800 max-w-[90px] xl:max-w-[120px] truncate">{user.name}</span>
          <i className={`hidden xl:inline-block ri-arrow-down-s-line text-slate-400 text-sm lg:text-base transition-transform duration-200 ${userMenuOpen ? 'rotate-180' : ''}`}></i>
        </button>
      </Tippy>
    </div>
  );
}

export default UserMenu;
